import React from 'react';
import { Modal, Button, Badge, Row, Col } from 'react-bootstrap';

/**
 * 감사로그 상세 정보 모달
 */
const AuditLogDetailModal = ({ show, onHide, log, loading }) => {
  if (!log) return null;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      'CRITICAL': 'danger',
      'HIGH': 'warning',
      'MEDIUM': 'info',
      'LOW': 'secondary'
    };
    return variants[severity] || 'secondary';
  };

  const getResultBadge = (result) => {
    const variants = {
      'SUCCESS': 'success',
      'FAILURE': 'danger',
      'WARNING': 'warning',
      'BLOCKED': 'dark'
    };
    return variants[result] || 'secondary';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'ACCESS_CONTROL': '접근 제어',
      'DATA_PROTECTION': '데이터 보호',
      'SYSTEM_SECURITY': '시스템 보안',
      'AUDIT_MONITORING': '감사 모니터링',
      'USER_MANAGEMENT': '사용자 관리',
      'BUSINESS_LOGIC': '비즈니스 로직',
      'SECURITY_VIOLATION': '보안 위반',
      'BIOMETRIC_AUTH': '생체 인증',
      'MEDICAL_DATA': '의료 데이터',
      'BIOMETRIC_MANAGEMENT': '생체정보 관리'
    };
    return labels[category] || category;
  };

  const renderJsonData = (data) => {
    if (!data) return '-';
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return <pre className="bg-light p-2 rounded" style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.85rem' }}>
        {JSON.stringify(parsed, null, 2)}
      </pre>;
    } catch (e) {
      return <div className="text-muted">{String(data)}</div>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>감사로그 상세 정보</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">로딩 중...</span>
            </div>
          </div>
        ) : (
          <>
            {/* 기본 정보 */}
            <h5 className="mb-3">기본 정보</h5>
            <Row className="mb-3">
              <Col md={6}>
                <div className="mb-2">
                  <strong>이벤트 ID:</strong>
                  <div className="text-muted font-monospace small">{log.event_id}</div>
                </div>
                <div className="mb-2">
                  <strong>발생 시간:</strong>
                  <div className="text-muted">{formatTimestamp(log.timestamp)}</div>
                </div>
                <div className="mb-2">
                  <strong>카테고리:</strong>
                  <div><Badge bg="info" className="text-white">{getCategoryLabel(log.category)}</Badge></div>
                </div>
                <div className="mb-2">
                  <strong>활동 유형:</strong>
                  <div className="text-muted">{log.activity_type}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <strong>심각도:</strong>
                  <div><Badge bg={getSeverityBadge(log.severity)} className="text-white">{log.severity}</Badge></div>
                </div>
                <div className="mb-2">
                  <strong>결과:</strong>
                  <div><Badge bg={getResultBadge(log.result)} className="text-white">{log.result}</Badge></div>
                </div>
                <div className="mb-2">
                  <strong>응답 코드:</strong>
                  <div className="text-muted">{log.response_code || '-'}</div>
                </div>
              </Col>
            </Row>

            <hr />

            {/* 사용자 정보 */}
            <h5 className="mb-3">사용자 정보</h5>
            <Row className="mb-3">
              <Col md={6}>
                <div className="mb-2">
                  <strong>사용자 ID:</strong>
                  <div className="text-muted">{log.user_id || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>사용자명:</strong>
                  <div className="text-muted">{log.username || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>권한:</strong>
                  <div className="text-muted">{log.user_role || '-'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <strong>세션 ID:</strong>
                  <div className="text-muted font-monospace small">{log.session_id || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>출발지 IP:</strong>
                  <div className="text-muted">{log.source_ip || '-'}</div>
                </div>
              </Col>
            </Row>

            <hr />

            {/* 요청 정보 */}
            <h5 className="mb-3">요청 정보</h5>
            <Row className="mb-3">
              <Col md={12}>
                <div className="mb-2">
                  <strong>User Agent:</strong>
                  <div className="text-muted small">{log.user_agent || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>요청 URI:</strong>
                  <div className="text-muted font-monospace small">{log.request_uri || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>HTTP 메서드:</strong>
                  <div className="text-muted">{log.request_method || '-'}</div>
                </div>
              </Col>
            </Row>

            <hr />

            {/* 리소스 정보 */}
            <h5 className="mb-3">리소스 정보</h5>
            <Row className="mb-3">
              <Col md={6}>
                <div className="mb-2">
                  <strong>리소스 유형:</strong>
                  <div className="text-muted">{log.resource_type || '-'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <strong>리소스 ID:</strong>
                  <div className="text-muted">{log.resource_id || '-'}</div>
                </div>
              </Col>
            </Row>

            <hr />

            {/* 상세 데이터 */}
            <h5 className="mb-3">상세 데이터</h5>
            
            {log.action_details && (
              <div className="mb-3">
                <strong>액션 상세:</strong>
                {renderJsonData(log.action_details)}
              </div>
            )}

            {log.before_data && (
              <div className="mb-3">
                <strong>변경 전 데이터:</strong>
                {renderJsonData(log.before_data)}
              </div>
            )}

            {log.after_data && (
              <div className="mb-3">
                <strong>변경 후 데이터:</strong>
                {renderJsonData(log.after_data)}
              </div>
            )}

            {log.additional_context && (
              <div className="mb-3">
                <strong>추가 컨텍스트:</strong>
                {renderJsonData(log.additional_context)}
              </div>
            )}

            <hr />

            {/* 무결성 정보 */}
            <h5 className="mb-3">무결성 정보</h5>
            <Row className="mb-3">
              <Col md={12}>
                <div className="mb-2">
                  <strong>서명:</strong>
                  <div className="text-muted font-monospace small text-break">{log.signature || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>서명 알고리즘:</strong>
                  <div className="text-muted">{log.signature_algorithm || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>서명 시간:</strong>
                  <div className="text-muted">{formatTimestamp(log.signature_timestamp)}</div>
                </div>
                <div className="mb-2">
                  <strong>키 버전:</strong>
                  <div className="text-muted">{log.key_version || '-'}</div>
                </div>
                <div className="mb-2">
                  <strong>NTP 타임스탬프:</strong>
                  <div className="text-muted">{formatTimestamp(log.ntp_timestamp)}</div>
                </div>
                <div className="mb-2">
                  <strong>NTP 동기화 상태:</strong>
                  <div className="text-muted">{log.ntp_sync_status || '-'}</div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          닫기
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AuditLogDetailModal;
